import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import get from "lodash.get";
import moment from "moment";
import styled from "@emotion/styled";

import { connect } from "react-redux";
import { useIntl } from "react-intl";

import Abilities from "services/permissions/abilities";
import ContractUtils from "services/contract/utils";
import MapActioner from "services/map/actioner";

import VisitorCharacteristicsActioner from "services/visitorCharacteristics/actioner";
import ParkkiTheme from "themes/ParkkiTheme";

import VisitorCharacteristicsFilters from "./components/VisitorCharacteristicsFilters";
import QualificationIndicatorsSection from "./components/QualificationIndicatorsSection";
import QualificationVisitorsSection from "./components/QualificationVisitorsSection";
import QualificationTimeSection from "./components/QualificationTimeSection";
import QualificationDestinationSection from "./components/QualificationDestinationSection";
import QualificationCatchmentAreasSection from "./components/QualificationCatchmentAreasSection";

const StyledContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: ${ParkkiTheme.colors.lightGrey};
`;

const GraphsContainer = styled.div`
  display: flex;
  display: 1;
  flex-direction: row;
`;

const LeftContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  max-width: 50%;
`;
const RightContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  max-width: 50%;
`;

function VisitorCharacteristics({
  setFilter,
  contract,
  filters,
  retrieveLayersInfos,
  retrieveAllStats,
  setQualificationIndicatorsFilters,
  setQualificationVisitorsFilters,
  setQualificationTimeFilters,
  setQualificationDestinationFilters,
  setCatchmentAreaCenterToDefault,
  setCatchmentAreasFilters,
  visitorCharacteristics,
  startDate,
  endDate,
}) {
  const contractId = contract?.id;
  const intl = useIntl();

  const initializeModuleData = useCallback(() => {
    if (contractId && !filters?.initialized) {
      retrieveLayersInfos();
    }
  }, [contractId, filters?.initialized, retrieveLayersInfos]);

  useEffect(() => {
    setTimeout(initializeModuleData, 200);
  }, [initializeModuleData]);

  function handleDateChange(key, date) {
    if (key === "from") {
      updateDate("from", date.startOf("day"));

      if (date.isAfter(moment(endDate), "day")) {
        updateDate("to", date.endOf("day"));
      }
    }

    if (key === "to") {
      updateDate("to", date.endOf("day"));

      if (date.isBefore(moment(startDate), "day")) {
        updateDate("from", date.startOf("day"));
      }
    }
  }

  function updateDate(key, date) {
    const timestamp = parseInt(date.valueOf() / 1000);
    return setFilter(key, timestamp);
  }

  return (
    <StyledContainer>
      <VisitorCharacteristicsFilters
        startDate={{
          value: startDate,
          label: intl.messages["flowScene.filters.date.start"],
          onChange: (date) => handleDateChange("from", date),
          maxDaysAllowed: ContractUtils.getFlowMaxDaysAllowed(contract),
        }}
        endDate={{
          value: endDate,
          label: intl.messages["flowScene.filters.date.end"],
          onChange: (date) => handleDateChange("to", date),
        }}
        area={{
          value: filters?.selectedArea,
          values: filters?.areas,
          onChange: (area) => setFilter("selectedArea", area),
        }}
        onSearch={retrieveAllStats}
      />

      {Abilities.ability.can(
        "read",
        "visitorCharacteristics.fullMapAppDistribution"
      ) ? (
        <QualificationCatchmentAreasSection
          fullScreen
          visitorCharacteristics={visitorCharacteristics}
          setCatchmentAreasFilters={setCatchmentAreasFilters}
          onRecenter={setCatchmentAreaCenterToDefault}
        />
      ) : null}

      <GraphsContainer>
        <LeftContainer>
          <QualificationIndicatorsSection
            visitorCharacteristics={visitorCharacteristics}
            setQualificationIndicatorsFilters={
              setQualificationIndicatorsFilters
            }
          />

          <QualificationVisitorsSection
            visitorCharacteristics={visitorCharacteristics}
            setQualificationVisitorsFilters={setQualificationVisitorsFilters}
          />

          <QualificationTimeSection
            visitorCharacteristics={visitorCharacteristics}
            setQualificationTimeFilters={setQualificationTimeFilters}
            averageTimeMaximumDuration={filters?.averageTimeMaximumDuration}
            contract={contract}
          />
        </LeftContainer>
        <RightContainer>
          {Abilities.ability.can(
            "read",
            "visitorCharacteristics.mapAppDistribution"
          ) ? (
            <QualificationCatchmentAreasSection
              visitorCharacteristics={visitorCharacteristics}
              setCatchmentAreasFilters={setCatchmentAreasFilters}
              onRecenter={setCatchmentAreaCenterToDefault}
            />
          ) : null}

          <QualificationDestinationSection
            visitorCharacteristics={visitorCharacteristics}
            setQualificationDestinationFilters={
              setQualificationDestinationFilters
            }
            destinationsDistribution={filters?.destinationsDistribution}
          />
        </RightContainer>
      </GraphsContainer>
    </StyledContainer>
  );
}

const mapStateToProps = (state) => ({
  contract: state.Contract.contract,
  visitorCharacteristics: state.VisitorCharacteristics,
  filters: state.VisitorCharacteristics.filters,
  startDate: get(state.VisitorCharacteristics, "filters.from") * 1000,
  endDate: get(state.VisitorCharacteristics, "filters.to") * 1000,
});

const mapDispatchToProps = (dispatch) => ({
  retrieveAllStats: () =>
    dispatch(VisitorCharacteristicsActioner.retrieveAllStats()),
  retrieveLayersInfos: () =>
    dispatch(VisitorCharacteristicsActioner.retrieveLayersInfos()),
  setFilter: (key, value) =>
    dispatch(VisitorCharacteristicsActioner.setFilter(key, value)),
  setQualificationIndicatorsFilters: (value) =>
    dispatch(
      VisitorCharacteristicsActioner.setQualificationIndicatorsFilters(value)
    ),
  setQualificationVisitorsFilters: (value) =>
    dispatch(
      VisitorCharacteristicsActioner.setQualificationVisitorsFilters(value)
    ),
  setQualificationTimeFilters: (value) =>
    dispatch(VisitorCharacteristicsActioner.setQualificationTimeFilters(value)),
  setQualificationDestinationFilters: (value) =>
    dispatch(
      VisitorCharacteristicsActioner.setQualificationDestinationFilters(value)
    ),
  setCatchmentAreasFilters: (value) =>
    dispatch(VisitorCharacteristicsActioner.setCatchmentAreasFilters(value)),
  setCatchmentAreaCenterToDefault: () =>
    dispatch(MapActioner.setCatchmentAreaCenterToDefault()),
});

VisitorCharacteristics.propTypes = {
  contract: PropTypes.object.isRequired,
  endDate: PropTypes.number.isRequired,
  visitorCharacteristics: PropTypes.object.isRequired,
  retrieveAllStats: PropTypes.func.isRequired,
  startDate: PropTypes.number.isRequired,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(VisitorCharacteristics);
